import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import PageIndicator from '@/components/PageIndicator';
import SignatureCanvas from '@/components/SignatureCanvas';
import { generateAndSavePagePDF } from '@/utils/pdfGenerator';
import html2pdf from 'html2pdf.js';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Local image paths to avoid CORS issues with html2canvas PDF generation
const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

const PageThree = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const clientData = location.state || {};
  
  // Get formId from previous page
  const formId = clientData.formId;
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Ref for signature canvas to restore signature before PDF generation
  const signatureCanvasRef = useRef(null);
  
  // Initialize with previous data if coming back from Page 4
  const [signature, setSignature] = useState(clientData.signaturePage3 || null);
  const [signedAt, setSignedAt] = useState(clientData.signedAtPage3 || '');
  const [showLocationWarning, setShowLocationWarning] = useState(false);
  const [locationError, setLocationError] = useState('');

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get today's date formatted
  const today = new Date();
  const dayOfMonth = today.getDate();
  const monthName = today.toLocaleDateString('en-ZA', { month: 'long' });
  const fullYear = today.getFullYear();

  // Construct full name from client data
  const getFullName = () => {
    const parts = [
      clientData.firstName,
      clientData.secondName,
      clientData.middleName,
      clientData.surname
    ].filter(Boolean);
    return parts.join(' ') || '[Client Full Name]';
  };

  const fullName = getFullName();

  // Get today's date for display
  const todayFormatted = today.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });

  // Validate location - only allow alphabetic letters and spaces
  const validateLocation = (value) => {
    if (!value || value.trim() === '') {
      return '';
    }
    // Check if the value contains only letters and spaces
    const locationRegex = /^[a-zA-Z\s]+$/;
    if (!locationRegex.test(value)) {
      return 'Location can only contain letters (no numbers or special characters)';
    }
    return '';
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setSignedAt(value);
    setLocationError(validateLocation(value));
  };

  const handleSignatureChange = (signatureData) => {
    setSignature(signatureData);
  };

  const handleClearSignature = () => {
    setSignature(null);
  };

  // Check if form is valid (location and signature required)
  const isFormValid = () => {
    return signedAt && signedAt.trim() !== '' && signature && !locationError;
  };

  const handleSubmit = async () => {
    if (!signedAt || signedAt.trim() === '') {
      alert('Please enter the location where you are signing.');
      return;
    }
    // Validate location before proceeding
    const locationValidationError = validateLocation(signedAt);
    if (locationValidationError) {
      alert(locationValidationError);
      return;
    }
    if (!signature) {
      alert('Please provide your signature before proceeding.');
      return;
    }
    
    // Check if location on Page 2 (SARS Power of Attorney) differs from Page 3 (POPI Consent Agreement)
    const page2Location = clientData.signedAtPage2 || '';
    const page3Location = signedAt.trim();
    
    if (page2Location && page3Location && page2Location.toLowerCase() !== page3Location.toLowerCase()) {
      setShowLocationWarning(true);
      return;
    }
    
    await generatePDFAndNavigate();
  };
  
  const generatePDFAndNavigate = async () => {
    // Restore signature to canvas before PDF generation (in case it was cleared by zoom/resize)
    if (signatureCanvasRef.current) {
      signatureCanvasRef.current.restoreSignature();
    }
    
    // Generate PDF of current page before navigating
    setIsGeneratingPDF(true);
    
    // Create full-screen overlay to hide PDF generation process from user
    const overlay = document.createElement('div');
    overlay.id = 'pdf-generation-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(255, 255, 255, 0.97);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 99999;
    `;
    overlay.innerHTML = `
      <div style="text-align: center;">
        <div style="width: 48px; height: 48px; border: 4px solid #e5e7eb; border-top-color: #f97316; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
        <p style="font-size: 18px; font-weight: 600; color: #1f2937; margin: 0;">Saving your progress...</p>
        <p style="font-size: 14px; color: #6b7280; margin-top: 8px;">Please wait while we generate your PDF</p>
      </div>
      <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
    `;
    document.body.appendChild(overlay);
    
    try {
      const element = document.querySelector('#page-pdf-content');
      if (!element) {
        console.error('[Page3] Element #page-pdf-content not found!');
        setIsGeneratingPDF(false);
        overlay.remove();
        return;
      }
      
      // Store original values for restoration
      const originalStyles = new Map();
      const hiddenElements = [];
      const originalClassNames = new Map();
      
      const hideElement = (el) => {
        if (el) {
          originalStyles.set(el, el.style.cssText);
          el.style.display = 'none';
          hiddenElements.push(el);
        }
      };
      
      const modifyStyle = (el, newStyle) => {
        if (el) {
          if (!originalStyles.has(el)) {
            originalStyles.set(el, el.style.cssText);
          }
          Object.assign(el.style, newStyle);
        }
      };
      
      const modifyClassName = (el, newClassName) => {
        if (el) {
          if (!originalClassNames.has(el)) {
            originalClassNames.set(el, el.className);
          }
          el.className = newClassName;
        }
      };
      
      // === PDF MODIFICATIONS FOR PAGE 3 ===
      // Keep the PDF's original top spacing (the page itself has only 2px above the logo; was py-6 sm:py-8)
      modifyStyle(element, { paddingTop: window.matchMedia('(min-width: 640px)').matches ? '2rem' : '1.5rem' });
      
      // Keep company logo, banner, and "TAX SERVICES AGREEMENT" heading visible
      
      // 1. Reduce top margin/padding
      modifyStyle(element, { paddingTop: '2px' });
      element.querySelectorAll('.py-6, .sm\\:py-8, .py-4').forEach(el => {
        modifyStyle(el, { paddingTop: '2px', paddingBottom: '2px' });
      });
      
      // 2. Reduce gap between headings
      element.querySelectorAll('.mb-6').forEach(el => {
        if (el.querySelector('h1')?.textContent?.includes('TAX SERVICES AGREEMENT')) {
          modifyStyle(el, { marginBottom: '4px', paddingBottom: '0px' });
        }
      });
      element.querySelectorAll('.text-center.mb-6').forEach(el => {
        if (el.querySelector('h2')?.textContent?.includes('POPI Consent Agreement')) {
          modifyStyle(el, { marginBottom: '6px', marginTop: '0px' });
        }
      });
      element.querySelectorAll('.p-4.sm\\:p-8').forEach(el => {
        modifyStyle(el, { paddingTop: '8px' });
      });
      element.querySelectorAll('.mb-4.sm\\:mb-6').forEach(el => {
        if (el.querySelector('img[alt="TCA Auditors Logo"]')) {
          modifyStyle(el, { marginBottom: '4px' });
        }
      });
      
      // 3. Compact layout spacing
      element.querySelectorAll('.space-y-4 > *:not(:first-child)').forEach(el => {
        modifyStyle(el, { marginTop: '6px' });
      });
      element.querySelectorAll('.space-y-6 > *:not(:first-child)').forEach(el => {
        modifyStyle(el, { marginTop: '8px' });
      });
      element.querySelectorAll('.space-y-8 > *:not(:first-child)').forEach(el => {
        modifyStyle(el, { marginTop: '10px' });
      });
      element.querySelectorAll('.mb-4').forEach(el => {
        modifyStyle(el, { marginBottom: '6px' });
      });
      element.querySelectorAll('.mb-6').forEach(el => {
        modifyStyle(el, { marginBottom: '8px' });
      });
      element.querySelectorAll('.mb-8').forEach(el => {
        modifyStyle(el, { marginBottom: '10px' });
      });
      
      // 4. Reduce padding on paragraphs
      element.querySelectorAll('p').forEach(el => {
        modifyStyle(el, { marginBottom: '3px', lineHeight: '1.5', display: 'block', whiteSpace: 'normal' });
      });
      element.querySelectorAll('h2, h3, h4').forEach(el => {
        modifyStyle(el, { marginBottom: '6px', marginTop: '8px' });
      });
      element.querySelectorAll('label').forEach(el => {
        modifyStyle(el, { marginBottom: '2px' });
      });
      
      // 5. Fix blue fields - inline with text
      element.querySelectorAll('.bg-blue-50, [class*="bg-blue"]').forEach(field => {
        if (field.tagName === 'INPUT') return;
        if (field.classList.contains('border-gray-400')) return;
        
        const newClassName = field.className.replace(/border-b-2/g, '').replace(/border-tca-blue/g, '');
        modifyClassName(field, newClassName);
        
        modifyStyle(field, { 
          display: 'inline-block', position: 'relative', top: '3px',
          padding: '0px 6px 5px 6px', margin: '0 2px', borderRadius: '3px',
          lineHeight: '1.3', verticalAlign: 'baseline', boxSizing: 'border-box',
          border: 'none', borderBottom: 'none', borderStyle: 'none',
          textDecoration: 'none', backgroundColor: '#dbeafe'
        });
      });
      
      // 6. Replace input fields with span showing their value
      element.querySelectorAll('input.bg-blue-50, input[class*="bg-blue"]').forEach(input => {
        const inputValue = input.value || input.placeholder || '';
        const replacementSpan = document.createElement('span');
        replacementSpan.textContent = inputValue;
        replacementSpan.style.cssText = `
          display: inline-block; position: relative; top: 3px;
          padding: 0px 6px 5px 6px; background-color: #dbeafe; color: #1e3a8a;
          font-weight: bold; border-radius: 3px; min-width: 80px;
          line-height: 1.3; vertical-align: baseline;
        `;
        replacementSpan.id = 'temp-input-replacement-' + Math.random().toString(36).substr(2, 9);
        input.style.display = 'none';
        input.parentNode.insertBefore(replacementSpan, input.nextSibling);
      });
      
      // 7. Signature canvas - LEFT ALIGNED and sized (increased by 20% total)
      element.querySelectorAll('.border-dashed, [class*="border-dashed"]').forEach(el => {
        if (el.querySelector('canvas') || el.textContent?.includes('Draw your signature')) {
          modifyStyle(el, { 
            width: '73%', maxWidth: '508px', height: '101px',
            minHeight: '101px', maxHeight: '101px', margin: '0', overflow: 'hidden'
          });
        }
      });
      element.querySelectorAll('canvas').forEach(canvas => {
        modifyStyle(canvas, { width: '100%', height: '88px', maxHeight: '88px' });
      });
      element.querySelectorAll('.h-40, .h-48, .sm\\:h-48').forEach(el => {
        if (el.closest('[class*="signature"]') || el.querySelector('canvas') || el.tagName === 'CANVAS') {
          modifyStyle(el, { height: '88px', minHeight: '88px', maxHeight: '88px' });
        }
      });
      element.querySelectorAll('.space-y-3').forEach(el => {
        if (el.querySelector('canvas')) {
          modifyStyle(el, { width: '73%', maxWidth: '508px', margin: '0' });
        }
      });
      
      // 8. Ensure company stamp is visible
      element.querySelectorAll('img[alt="TC Auditors Company Stamp"]').forEach(img => {
        modifyStyle(img, { display: 'inline-block', width: '222px', height: 'auto', objectFit: 'contain' });
      });
      
      // 9. Hide <br> tags
      element.querySelectorAll('br').forEach(br => {
        modifyStyle(br, { display: 'none' });
      });
      
      // 10. Reduce border-t section spacing
      element.querySelectorAll('.border-t').forEach(el => {
        modifyStyle(el, { marginTop: '8px', paddingTop: '8px' });
      });
      
      // 11. Reduce list item spacing
      element.querySelectorAll('ul').forEach(ul => {
        modifyStyle(ul, { marginTop: '4px', marginBottom: '4px' });
      });
      element.querySelectorAll('li').forEach(li => {
        modifyStyle(li, { marginBottom: '2px', lineHeight: '1.4' });
      });
      
      // 12. Add page break before "7. Conclusion"
      element.querySelectorAll('h3, p, div').forEach(el => {
        if (el.textContent?.trim().startsWith('7. Conclusion') || el.textContent?.trim() === '7. Conclusion') {
          modifyStyle(el, { pageBreakBefore: 'always', breakBefore: 'page', marginTop: '0px', paddingTop: '20px' });
        }
      });
      
      // Hide elements marked for PDF exclusion
      element.querySelectorAll('[data-hide-in-pdf="true"]').forEach(el => hideElement(el));
      
      // SHOW elements that are PDF-only (hidden on website, visible in PDF)
      element.querySelectorAll('[data-pdf-only="true"]').forEach(el => {
        el.style.display = 'flex';
      });
      
      // Add PDF footer with page indicator style (page name, page number, TCA logo)
      const footer = document.createElement('div');
      footer.id = 'temp-pdf-footer';
      footer.style.cssText = `
        width: 100%;
        padding: 16px 0 8px 0;
        margin-top: 20px;
        border-top: 1px solid #e5e7eb;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
      `;
      footer.innerHTML = `
        <div style="font-size: 14px; color: #374151; font-weight: 500; margin-bottom: 8px;">
          POPI Consent Agreement
        </div>
        <div style="font-size: 13px; color: #6b7280;">
          Document <strong style="color: #111827;">03</strong> of <strong style="color: #111827;">07</strong>
        </div>
        <img src="/images/TCA_Short_Logo.png" alt="TCA Logo" style="position: absolute; right: -6px; top: 50%; transform: translateY(-50%); height: 48px; width: auto; opacity: 0.8;" />
      `;
      element.appendChild(footer);
      
      // Generate PDF
      const pdfOptions = {
        margin: [4, 6, 4, 6],
        filename: `Page03_${formId || 'form'}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 2, useCORS: true, logging: false, letterRendering: true,
          scrollX: 0, scrollY: 0, windowWidth: element.scrollWidth
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      
      const pdfBlob = await html2pdf().set(pdfOptions).from(element).outputPdf('blob');
      
      // === RESTORE ORIGINAL STATE ===
      const tempFooter = document.getElementById('temp-pdf-footer');
      if (tempFooter) tempFooter.remove();
      element.querySelectorAll('[id^="temp-input-replacement-"]').forEach(span => span.remove());
      element.querySelectorAll('input').forEach(input => { 
        if (input.style.display === 'none') input.style.display = ''; 
      });
      originalClassNames.forEach((origClassName, el) => { el.className = origClassName; });
      originalStyles.forEach((origStyle, el) => { el.style.cssText = origStyle; });
      
      // Save to backend
      if (formId) {
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(pdfBlob);
        });
        
        const MAX_RETRIES = 3;
        let saveSuccessful = false;
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            console.log(`[Page3] Save attempt ${attempt}/${MAX_RETRIES}...`);
            
            const response = await fetch(`${API_URL}/api/pdf/save-page`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                form_id: formId, 
                page_number: 3, 
                pdf_data: base64Data,
                page_title: 'FICA Confirmation & POPIA Consent',
                client_data: clientData
              })
            });
            
            if (!response.ok) {
              throw new Error(`Server returned ${response.status}`);
            }
            
            // Verify the PDF was saved
            const verifyResponse = await fetch(`${API_URL}/api/pdf/debug/${formId}`);
            if (verifyResponse.ok) {
              const verifyData = await verifyResponse.json();
              const page3Saved = verifyData.pages?.some(p => p.page_number === 3);
              if (page3Saved) {
                console.log('[Page3] PDF saved and verified successfully');
                saveSuccessful = true;
                break;
              } else {
                throw new Error('PDF not found after save');
              }
            }
          } catch (err) {
            console.error(`[Page3] Save attempt ${attempt} failed:`, err.message);
            if (attempt < MAX_RETRIES) {
              await new Promise(r => setTimeout(r, 2000));
            }
          }
        }
        
        if (!saveSuccessful) {
          const overlayEl = document.getElementById('pdf-generation-overlay');
          if (overlayEl) overlayEl.remove();
          setIsGeneratingPDF(false);
          alert('Failed to save your progress after multiple attempts. Please check your internet connection and try again.');
          return;
        }
      }
      
      // Remove overlay
      const overlayEl = document.getElementById('pdf-generation-overlay');
      if (overlayEl) overlayEl.remove();
      
      setIsGeneratingPDF(false);
      
      // Navigate to Page 4
      navigate('/page-4', { 
        state: { 
          ...clientData,
          signedAtPage3: signedAt,
          signaturePage3: signature
        } 
      });
      
    } catch (error) {
      console.error('[Page3] PDF generation error:', error);
      const overlayEl = document.getElementById('pdf-generation-overlay');
      if (overlayEl) overlayEl.remove();
      setIsGeneratingPDF(false);
      alert('An error occurred while generating your PDF. Please try again.');
    }
  };
  
  const handleProceedAnyway = async () => {
    setShowLocationWarning(false);
    await generatePDFAndNavigate();
  };

  const handleBack = () => {
    // Pass all the client data back to Page 2, including Page 3's current data
    navigate('/page-2', { 
      state: {
        ...clientData,
        signedAtPage3: signedAt,
        signaturePage3: signature
      }
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Main Content - wrapped for PDF capture */}
      <main id="page-pdf-content" className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-6 sm:pb-8 bg-white">
        {/* Logo Section */}
        <div className="mb-4 sm:mb-6 flex flex-col items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="TCA Auditors Logo" 
            className="h-16 sm:h-20 w-auto object-contain"
          />
          <img 
            src={BANNER_URL} 
            alt="Tax & Auditing Consultants" 
            className="h-10 sm:h-14 w-auto object-contain"
          />
        </div>

        {/* Main Heading */}
        <div className="mb-6 pb-2">
          <h1 className="text-xl sm:text-2xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            TAX SERVICES AGREEMENT
          </h1>
        </div>

        {/* POPI Consent Agreement Document */}
        <Card className="w-full max-w-4xl border shadow-card">
          <CardContent className="p-4 sm:p-8">
            {/* Document Header */}
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 underline">POPI Consent Agreement</h2>
            </div>

            {/* Parties Section */}
            <div className="space-y-2 mb-6 text-sm sm:text-base text-gray-800">
              <p>
                <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{fullName}</span> ("The Client")
              </p>
              <p className="text-center font-semibold">And</p>
              <p><strong>TC Auditors (Pty) Ltd</strong> ("the Company")</p>
              <p className="mt-4">
                On <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{todayFormatted}</span> ("Date of Agreement")
              </p>
            </div>

            {/* Preamble Section */}
            <div className="space-y-4 mb-6 text-sm sm:text-base text-gray-800 leading-relaxed">
              <h3 className="font-bold text-lg underline">1. Preamble</h3>
              <p>
                <span className="font-semibold">1.1</span> the Client and Company hereby conclude an agreement relating to a POPI Consent Agreement ("The Agreement") and this said agreement contains the following provisions:
              </p>
            </div>

            {/* Introduction Section */}
            <div className="space-y-4 text-sm sm:text-base text-gray-800 leading-relaxed">
              <h3 className="font-bold text-lg underline">2. Introduction</h3>
              
              <p>
                <span className="font-semibold">2.1</span> This agreement will be applicable to all personal information as defined in the Protection of Personal 
                Information Act, 4 of 2013 ("POPI"). By either Party submitting any personal information to the other, 
                the disclosing Party unconditionally and voluntarily, consents to the processing of the submitted 
                personal information for any and all purposes related to this agreement.
              </p>

              <h3 className="font-bold text-lg underline">3. Recitals</h3>

              <p>
                <span className="font-semibold">3.1</span> The Parties agrees and consent that its personal information may be processed by, or on behalf of 
                either of the Parties for the purposes set out in the Agreement.
              </p>

              <p>
                <span className="font-semibold">3.2</span> The Parties shall at all times comply with its obligations and procure that each of its Affiliates 
                comply with their obligations under POPI.
              </p>

              <p>
                <span className="font-semibold">3.3</span> The Parties shall ensure that any personal information that is processed by it in the course of 
                performing its obligations under the Agreement is done in accordance with POPI.
              </p>

              <h3 className="font-bold text-lg underline">4. Provisions</h3>

              <p><span className="font-semibold">4.1</span> Each Party shall not process, disclose, or use personal information except:</p>
              <ul className="list-disc list-inside ml-8 space-y-1">
                <li>to the extent necessary for the provision of Services and/or Products under the Agreement; or</li>
                <li>to fulfil their own obligations under the Agreement; or</li>
                <li>as otherwise expressly authorised by the other Party in writing.</li>
              </ul>

              <p>
                <span className="font-semibold">4.2</span> Each Party shall not disclose any personal information to any Third Party without the other Party's 
                prior written consent in each instance, other than to the extent required by any Regulator or Law.
              </p>

              <p>
                <span className="font-semibold">4.3</span> In the event the other Party providing such consent necessary for the disclosure of personal information 
                to a Third Party, each Party shall:
              </p>
              <ul className="list-disc list-inside ml-8 space-y-1">
                <li>make such disclosure in compliance with POPI; and</li>
                <li>enter into a written agreement with the applicable Third-Party recipient of such personal information 
                that requires such Third Party to safeguard the personal information in a manner no less restrictive 
                than each Party's obligations under these terms.</li>
              </ul>

              <p>
                <span className="font-semibold">4.4</span> The Parties shall implement and maintain an effective security safeguards that includes, but is not 
                limited to administrative, technical, and physical safeguards, and appropriate technical and 
                organisational measures, in each case, adequate to insure the security and confidentiality of personal 
                information, and to protect against any anticipated risks to the security or integrity of personal 
                information, protect against unauthorized access to or use of personal information, protect personal 
                information against unlawful processing or processing otherwise than in accordance with this agreement, 
                and protect against accidental loss, destruction, damage, alteration or disclosure of personal information.
              </p>

              <p>
                <span className="font-semibold">4.5</span> Without limiting the foregoing, such safeguards and measures shall be appropriate to protect against the 
                harm that may result from unauthorised or unlawful processing, use or disclosure, or accidental loss, 
                destruction, or damage to or of Personal Information and the nature of the personal information, and 
                shall maintain all safeguard measures as is required by POPI.
              </p>

              <h3 className="font-bold text-lg underline">5. Cross Border</h3>

              <p>
                <span className="font-semibold">5.1</span> Each Party shall not use, process, store, transfer or permit access to any personal information across 
                the borders of South Africa, without the written consent of the other Party.
              </p>

              <h3 className="font-bold text-lg underline">6. Breach</h3>

              <p>
                <span className="font-semibold">6.1</span> In the event of any actual, suspected, or alleged security breach, including, but not limited to, loss, 
                damage, destruction, theft, unauthorized use, access to or disclosure of any personal information, each 
                Party shall:
              </p>
              <ul className="list-disc list-inside ml-8 space-y-1">
                <li>notify the other Party as soon as practicable after becoming aware of such event.</li>
                <li>provide the other Party will all information regarding the breach in the Party's knowledge and 
                possession to allow the Party to ascertain what has occurred and which personal information has been affected.</li>
                <li>promptly take whatever action is necessary, at each Party's own expense, to minimise the impact of 
                such event and prevent such event from recurring.</li>
              </ul>

              <h3 className="font-bold text-lg underline">7. Conclusion</h3>

              <p>
                <span className="font-semibold">7.1</span> The client hereby consents to the Company sharing the personal information as provided for herein cross 
                border. Should the client's personal information be shared cross border, the personal information will 
                not be subject to less protection than it enjoys in terms of South Africa's data privacy laws.
              </p>
            </div>

            <br />
            <br />

            {/* Client Signature Section */}
            <div className="border-t pt-6 mt-6">
              <h3 className="font-bold text-lg underline mb-4">Accepted by client by virtue of signature</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Client name:</span>
                  <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{fullName}</span>
                </div>

                {/* Client Signature */}
                <div>
                  <p className="font-semibold mb-3">Client signature:</p>
                  <SignatureCanvas 
                    ref={signatureCanvasRef}
                    onSignatureChange={handleSignatureChange}
                    onClear={handleClearSignature}
                    heightClass="h-40 sm:h-48"
                    initialSignature={signature}
                  />
                </div>

                <br />

                {/* Signed at Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p>
                    Signed at <input 
                      type="text" 
                      value={signedAt}
                      onChange={handleLocationChange}
                      onBlur={(e) => setSignedAt(e.target.value.trim())}
                      placeholder="Enter location"
                      className="border-b-2 border-tca-blue bg-blue-50 px-2 py-1 w-40 focus:outline-none font-bold text-tca-blue rounded"
                    /> on this <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{dayOfMonth}</span> day of <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{monthName} {fullYear}</span>
                  </p>
                  {locationError && <p className="text-sm text-red-600 mt-2">{locationError}</p>}
                </div>
              </div>
            </div>

            <br />
            <br />
            <br />

            {/* Company Section */}
            <div className="border-t pt-6">
              <h3 className="font-bold text-lg underline mb-4">Accepted by Company by virtue of Director signature + Company Stamp</h3>
              <div className="space-y-2">
                <p><span className="font-semibold">Company name:</span> <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">TC Auditors (Pty) Ltd</span></p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold whitespace-nowrap">Company stamp:</span>
                    <img 
                      src="/images/TCA_Company_Stamp.webp" 
                      alt="TC Auditors Company Stamp" 
                      className="w-[222px] h-auto object-contain"
                      data-pdf-only="true"
                      style={{ display: 'none' }}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold whitespace-nowrap">Director signature:</span>
                    <img 
                      src="/images/Director_Signature.png" 
                      alt="Director Signature" 
                      className="h-24 w-auto object-contain"
                      data-pdf-only="true"
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              </div>
              
              <br />
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <p>
                  Signed at <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">Meyerton</span> on this <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{dayOfMonth}</span> day of <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{monthName} {fullYear}</span>
                </p>
              </div>
            </div>

            <br />
            <br />
            <br />
            <br />

            {/* Action Buttons - Hidden in PDF */}
            <div className="flex flex-col sm:flex-row gap-3 items-center mt-8 pt-6 border-t" data-hide-in-pdf="true">
              <a 
                href="https://tax.tcauditors.co.za"
                className="inline-flex items-center justify-center text-xs font-bold px-4 py-2 h-9 rounded-md bg-slate-600 hover:bg-slate-700 text-white transition-all duration-200 shadow-sm"
                data-hide-in-pdf="true"
              >
                Start Over
              </a>
              <Button 
                type="button"
                variant="outline"
                onClick={handleBack}
                className="sm:w-auto"
              >
                ← Back to Page 2
              </Button>
              
              <Button 
                type="button"
                onClick={handleSubmit}
                disabled={!isFormValid() || isGeneratingPDF}
                className="sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-8 h-12 text-base rounded-lg shadow-soft hover:shadow-card transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingPDF ? 'Saving...' : 'Go to Next Page...'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Page Indicator with TCA Logo */}
      <div className="relative">
        <PageIndicator currentPage={3} totalPages={7} pageTitle="POPI Consent Agreement" />
        <img 
          src={TCA_SHORT_LOGO} 
          alt="TCA Logo" 
          className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80"
        />
      </div>

      {/* Location Discrepancy Warning Modal */}
      <AlertDialog open={showLocationWarning} onOpenChange={setShowLocationWarning}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-600 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Location Discrepancy
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm">
                <p>
                  The location you entered on this page does not match the location on the previous page. 
                  Please ensure consistency across all pages.
                </p>
                <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">SARS Power of Attorney:</span>
                    <span className="font-bold text-tca-blue">{clientData.signedAtPage2 || 'Not entered'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">POPI Consent Agreement:</span>
                    <span className="font-bold text-tca-blue">{signedAt}</span>
                  </div>
                </div>
                <p className="text-gray-600">
                  Please go back and correct the location on either the <strong>SARS Power of Attorney</strong> or the <strong>POPI Consent Agreement</strong> to be consistent.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowLocationWarning(false)}>
              OK, I'll correct it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PageThree;
